import { computed, signal } from "@preact/signals-core";
import qrcode from "qrcode-generator";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, bindValue, html, list } from "../lib/bind";
import { copyText } from "../lib/clipboard";
import { form } from "../lib/form";
import { addPasswordToggle } from "../lib/passwordField";
import { currentScope } from "../lib/scope";
import { toast } from "../lib/toast";
import {
  confirmTotp,
  changePassword,
  fetchMe,
  regenerateRecoveryCodes,
  setupTotp,
} from "../services/auth";

function qrDataUrl(provisioningUri: string): string {
  const qr = qrcode(0, "M");
  qr.addData(provisioningUri);
  qr.make();
  return qr.createDataURL(6, 4);
}

export function settingsView(): Node {
  const totpEnabled = signal<boolean | null>(null); // null while loading
  const totpStage = signal<"idle" | "enrolling" | "confirming" | "done">("idle");
  const qrUrl = signal("");
  const secret = signal("");
  const recoveryCodes = signal<string[]>([]);

  const passwordForm = form({ currentPassword: "", newPassword: "" });
  const passwordError = signal("");

  const enrolCodeForm = form({ code: "" });
  const enrolError = signal("");

  const regenForm = form({ password: "", code: "" });
  const regenError = signal("");
  const regenCodes = signal<string[]>([]);

  fetchMe()
    .then((me) => {
      totpEnabled.value = me.totp_enabled;
    })
    .catch(() => {
      totpEnabled.value = false;
    });

  const showEnableButton = computed(
    () => totpEnabled.value === false && totpStage.value === "idle",
  );
  const showEnrolling = computed(() => totpStage.value === "enrolling");
  const showRecoveryCodes = computed(
    () => totpStage.value === "done" && recoveryCodes.value.length > 0,
  );
  const showEnabledPanel = computed(
    () => totpEnabled.value === true && totpStage.value !== "done",
  );

  async function submitPasswordChange(event: Event): Promise<void> {
    event.preventDefault();
    passwordError.value = "";
    try {
      await changePassword(
        passwordForm.fields.currentPassword.value,
        passwordForm.fields.newPassword.value,
      );
      passwordForm.reset({ currentPassword: "", newPassword: "" });
      toast.success("Password changed. Other sessions have been logged out.");
    } catch (err) {
      passwordError.value = err instanceof ApiRequestError ? err.body.message : "Failed";
      toast.error(passwordError.value);
    }
  }

  async function startEnrolment(): Promise<void> {
    try {
      const setup = await setupTotp();
      secret.value = setup.secret;
      qrUrl.value = qrDataUrl(setup.provisioning_uri);
      totpStage.value = "enrolling";
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Could not start 2FA setup");
    }
  }

  async function submitEnrolCode(event: Event): Promise<void> {
    event.preventDefault();
    enrolError.value = "";
    try {
      const codes = await confirmTotp(enrolCodeForm.fields.code.value);
      recoveryCodes.value = codes;
      totpEnabled.value = true;
      totpStage.value = "done";
      toast.success("Two-factor authentication enabled.");
    } catch (err) {
      enrolError.value = err instanceof ApiRequestError ? err.body.message : "Invalid code";
      toast.error(enrolError.value);
    }
  }

  async function copyCodes(codes: string[]): Promise<void> {
    const copied = await copyText(codes.join("\n"));
    if (copied) toast.success("Recovery codes copied to the clipboard.");
    else toast.error("Could not access the clipboard — select the codes and copy manually.");
  }

  async function submitRegenerate(event: Event): Promise<void> {
    event.preventDefault();
    regenError.value = "";
    try {
      const codes = await regenerateRecoveryCodes(
        regenForm.fields.password.value,
        regenForm.fields.code.value,
      );
      regenCodes.value = codes;
      regenForm.reset({ password: "", code: "" });
      toast.success("Recovery codes regenerated.");
    } catch (err) {
      regenError.value = err instanceof ApiRequestError ? err.body.message : "Failed";
      toast.error(regenError.value);
    }
  }

  const fragment = html`<div class="view">
    <div class="card-grid">
      <div class="card">
        <div class="card-label">Change password</div>
        <form class="form password-form" onsubmit="${submitPasswordChange}">
          <div class="form-field">
            <label for="current-password">Current password</label>
            <input id="current-password" type="password" autocomplete="current-password" required />
          </div>
          <div class="form-field">
            <label for="new-password">New password (min 12 characters)</label>
            <input id="new-password" type="password" autocomplete="new-password" required />
          </div>
          <p class="form-error password-error"></p>
          <div class="form-actions">
            <button class="btn btn-primary" type="submit">Change password</button>
          </div>
        </form>
      </div>

      <div class="card totp-card">
        <div class="card-label">Two-factor authentication</div>

        <div class="totp-enable-panel">
          <p>Two-factor authentication is not yet enabled on this account.</p>
          <button class="btn btn-primary" type="button" onclick="${startEnrolment}">
            Enable 2FA
          </button>
        </div>

        <div class="totp-enrolling-panel">
          <p>Scan this QR code with your authenticator app, or enter the secret manually.</p>
          <div class="totp-qr"><img class="totp-qr-img" alt="TOTP QR code" /></div>
          <p class="totp-secret"></p>
          <form class="form enrol-code-form" onsubmit="${submitEnrolCode}">
            <div class="form-field">
              <label for="enrol-code">Enter the 6-digit code to confirm</label>
              <input id="enrol-code" autocomplete="one-time-code" inputmode="numeric" required />
            </div>
            <p class="form-error enrol-error"></p>
            <div class="form-actions">
              <button class="btn btn-primary" type="submit">Confirm</button>
            </div>
          </form>
        </div>

        <div class="totp-recovery-panel">
          <p><strong>Save these recovery codes now — they are shown only once.</strong></p>
          <ul class="recovery-codes recovery-codes-list"></ul>
          <div class="form-actions">
            <button
              class="btn copy-recovery-codes"
              type="button"
              onclick="${() => void copyCodes(recoveryCodes.value)}"
            >
              Copy codes
            </button>
          </div>
        </div>

        <div class="totp-enabled-panel">
          <p>Two-factor authentication is enabled.</p>
          <p>Regenerate recovery codes (requires current password + a current code):</p>
          <form class="form regen-form" onsubmit="${submitRegenerate}">
            <div class="form-row">
              <div class="form-field">
                <label for="regen-password">Password</label>
                <input id="regen-password" type="password" autocomplete="current-password" required />
              </div>
              <div class="form-field">
                <label for="regen-code">Current code</label>
                <input id="regen-code" autocomplete="one-time-code" inputmode="numeric" required />
              </div>
            </div>
            <p class="form-error regen-error"></p>
            <div class="form-actions">
              <button class="btn" type="submit">Regenerate recovery codes</button>
            </div>
          </form>
          <ul class="recovery-codes regen-codes-list"></ul>
          <div class="form-actions regen-codes-actions">
            <button
              class="btn copy-regen-codes"
              type="button"
              onclick="${() => void copyCodes(regenCodes.value)}"
            >
              Copy codes
            </button>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-label">Audit log</div>
        <p>Review a filterable history of invoice, payment, VAT, banking, and loan events.</p>
        <a href="/audit-log" data-link class="btn">View audit log</a>
      </div>

      <div class="card">
        <div class="card-label">Backups</div>
        <p>
          Check when the last verified backup ran, take one now, download it, or export this
          company's data as JSON. Restoring stays on the command line.
        </p>
        <a href="/backups" data-link class="btn">Manage backups</a>
      </div>
    </div>
  </div>`;

  const passwordErrorEl = fragment.querySelector<HTMLParagraphElement>(".password-error");
  const enablePanel = fragment.querySelector<HTMLElement>(".totp-enable-panel");
  const enrollingPanel = fragment.querySelector<HTMLElement>(".totp-enrolling-panel");
  const recoveryPanel = fragment.querySelector<HTMLElement>(".totp-recovery-panel");
  const enabledPanel = fragment.querySelector<HTMLElement>(".totp-enabled-panel");
  const qrImg = fragment.querySelector<HTMLImageElement>(".totp-qr-img");
  const secretEl = fragment.querySelector<HTMLParagraphElement>(".totp-secret");
  const enrolCodeInput = fragment.querySelector<HTMLInputElement>("#enrol-code");
  const enrolErrorEl = fragment.querySelector<HTMLParagraphElement>(".enrol-error");
  const recoveryList = fragment.querySelector<HTMLUListElement>(".recovery-codes-list");
  const regenPasswordInput = fragment.querySelector<HTMLInputElement>("#regen-password");
  const regenCodeInput = fragment.querySelector<HTMLInputElement>("#regen-code");
  const regenErrorEl = fragment.querySelector<HTMLParagraphElement>(".regen-error");
  const regenCodesList = fragment.querySelector<HTMLUListElement>(".regen-codes-list");
  const regenCodesActions = fragment.querySelector<HTMLElement>(".regen-codes-actions");
  const currentPasswordInput = fragment.querySelector<HTMLInputElement>("#current-password");
  const newPasswordInput = fragment.querySelector<HTMLInputElement>("#new-password");

  if (
    !passwordErrorEl ||
    !enablePanel ||
    !enrollingPanel ||
    !recoveryPanel ||
    !enabledPanel ||
    !qrImg ||
    !secretEl ||
    !enrolCodeInput ||
    !enrolErrorEl ||
    !recoveryList ||
    !regenPasswordInput ||
    !regenCodeInput ||
    !regenErrorEl ||
    !regenCodesList ||
    !regenCodesActions ||
    !currentPasswordInput ||
    !newPasswordInput
  ) {
    throw new Error("settings view markup missing expected elements");
  }

  bindValue(currentPasswordInput, passwordForm.fields.currentPassword);
  bindValue(newPasswordInput, passwordForm.fields.newPassword);
  addPasswordToggle(currentPasswordInput);
  addPasswordToggle(newPasswordInput);
  bindErrorText(passwordErrorEl, passwordError);

  bindValue(enrolCodeInput, enrolCodeForm.fields.code);
  bindErrorText(enrolErrorEl, enrolError);
  bindText(secretEl, secret);

  bindValue(regenPasswordInput, regenForm.fields.password);
  bindValue(regenCodeInput, regenForm.fields.code);
  addPasswordToggle(regenPasswordInput);
  bindErrorText(regenErrorEl, regenError);

  bindShow(enablePanel, showEnableButton);
  bindShow(enrollingPanel, showEnrolling);
  bindShow(recoveryPanel, showRecoveryCodes);
  bindShow(enabledPanel, showEnabledPanel);
  bindShow(
    regenCodesActions,
    computed(() => regenCodes.value.length > 0),
  );

  // <li>, not <span>: Firefox serialises a selection over specified-inline elements with
  // no separator whatever the grid layout does, so drag-selecting the codes and pasting
  // gave one run-on string — 10 unusable codes. Chromium emits newlines either way, which
  // is why this only reproduces in Firefox.
  const renderCode = (code: string): Node => {
    const el = document.createElement("li");
    el.className = "recovery-code";
    el.textContent = code;
    return el;
  };
  list(recoveryList, recoveryCodes, (code) => code, renderCode);
  list(regenCodesList, regenCodes, (code) => code, renderCode);

  currentScope().effect(() => {
    qrImg.src = qrUrl.value;
  });

  return fragment;
}
